/**
 * Helper functions to analyze capacity snapshots for useful metrics
 */

import { CapacitySnapshot, ProviderCapacityDay, RoleCapacityDay } from '../types/capacity.types';

/**
 * Get count of high-risk provider-days
 */
export function getHighRiskCount(snapshot: CapacitySnapshot): number {
  return snapshot.providers.filter((day) => day.riskLevel === 'high').length;
}

/**
 * Get count of over-capacity provider-days
 */
export function getOverCapacityCount(snapshot: CapacitySnapshot): number {
  return snapshot.providers.filter((day) => day.isOverCapacity).length;
}

/**
 * Get unique roles in the snapshot
 */
export function getUniqueRoles(snapshot: CapacitySnapshot): string[] {
  const roles = new Set(snapshot.providers.map((day) => day.role));
  return Array.from(roles).sort();
}

/**
 * Get unique dates in the snapshot
 */
export function getUniqueDates(snapshot: CapacitySnapshot): string[] {
  const dates = new Set(snapshot.providers.map((day) => day.date));
  return Array.from(dates).sort();
}

/**
 * Get role-days with coverage gaps (negative surplus)
 */
export function getCoverageGapCount(snapshot: CapacitySnapshot): number {
  return snapshot.roles.filter((roleDay) => roleDay.gapMinutes < 0).length;
}

/**
 * Get summary statistics for the snapshot
 */
export function getSnapshotSummary(snapshot: CapacitySnapshot) {
  const uniqueRoles = getUniqueRoles(snapshot);
  const uniqueDates = getUniqueDates(snapshot);
  const highRiskCount = getHighRiskCount(snapshot);
  const overCapacityCount = getOverCapacityCount(snapshot);
  const coverageGaps = getCoverageGapCount(snapshot);

  return {
    roles: uniqueRoles,
    roleCount: uniqueRoles.length,
    dateCount: uniqueDates.length,
    providerDayCount: snapshot.providers.length,
    highRiskCount,
    overCapacityCount,
    coverageGaps,
    hasIssues: highRiskCount > 0 || overCapacityCount > 0 || coverageGaps > 0,
  };
}
